import EditAssignmentDialog from './EditAssignmentDialog';
import AssignmentRelationLinks from './AssignmentRelationLinks';

import React, { useEffect, useState } from 'react';
import EntityDetailDialog from '@/shared/components/EntityDetailDialog';
import { IconActionButton } from '@/shared/components/patterns';
import '../../styles/assignment-detail.css';
import {
  formatDate,
  isAssignmentAssignedToCurrentUser,
  isAssignmentRequestedByCurrentUser,
} from '@/modules/deals/utils';
import AssignmentAttachments from './AssignmentAttachments';
import AssignmentComments from './AssignmentComments';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import {
  archiveDealsCustomField,
  createDealsCustomField,
  listDealsCustomFields,
} from '../../services/deals-custom-fields.service';

const ASSIGNMENT_CUSTOM_FIELDS_API = {
  list: listDealsCustomFields,
  create: createDealsCustomField,
  archive: archiveDealsCustomField,
};

const STATUS_LABELS = {
  da_accettare: 'Da accettare',
  in_corso: 'In corso',
  completato: 'Completato',
  rifiutato: 'Rifiutato',
};

const TYPE_LABELS = {
  gestione_affare: 'Gestione affare',
  gestione_cliente: 'Gestione cliente',
  documentazione: 'Documentazione',
  altro: 'Altro',
};

const URGENCY_LABELS = {
  alta: 'Alta',
  media: 'Media',
  bassa: 'Bassa',
};

function getInitials(value = '') {
  const parts = String(value).trim().split(/\s+/).filter(Boolean);
  return parts.length
    ? parts
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase()
    : 'TK';
}

function AssignmentField({ label, children, wide = false }) {
  return (
    <div className={`assignment-detail__field${wide ? ' assignment-detail__field--wide' : ''}`}>
      <dt>{label}</dt>
      <dd className="select-text">{children || '—'}</dd>
    </div>
  );
}

export default function AssignmentDetailDialog({
  open,
  onOpenChange,
  assignmentId,
  focusComments = false,
  assignment,
  currentUser,
  staff = [],
  clients = [],
  suppliers = [],
  actionState = {},
  isLoading = false,
  loadError = '',
  canDelete = false,
  canComplete = false,
  onDelete,
  onAccept,
  onReject,
  onComplete,
  onEdit,
  onSave,
}) {
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(false);
  }, [assignment?.id, open]);
  const isActionLoading = Boolean(actionState.loading);
  const canRespond = Boolean(
    assignment &&
    assignment.status === 'da_accettare' &&
    isAssignmentAssignedToCurrentUser(assignment, currentUser) &&
    !isAssignmentRequestedByCurrentUser(assignment, currentUser)
  );
  const title =
    assignment?.title || assignment?.description || (isLoading ? 'Caricamento task…' : 'Dettaglio task');
  const linkedEntity =
    assignment?.clientName || assignment?.supplierName || (assignment ? 'Task generale' : '');
  const hasFooterActions = Boolean(
    canRespond ||
    (canComplete && assignment?.status === 'in_corso') ||
    onSave ||
    onEdit ||
    (canDelete && assignment?.status === 'da_accettare')
  );
  const footer =
    assignment && hasFooterActions ? (
      <>
        {canRespond ? (
          <IconActionButton
            iconKey="actions.resend"
            label={
              actionState.action === 'accept' && isActionLoading ? 'Accettazione in corso' : 'Accetta task'
            }
            disabled={isActionLoading}
            onClick={() => onAccept?.(assignment.id)}
          />
        ) : null}
        {canComplete && assignment.status === 'in_corso' ? (
          <IconActionButton
            iconKey="actions.complete"
            tone="success"
            label={
              actionState.action === 'complete' && isActionLoading
                ? 'Completamento in corso'
                : 'Completa task'
            }
            disabled={isActionLoading}
            onClick={() => onComplete?.(assignment.id)}
          />
        ) : null}
        {onSave ? (
          <IconActionButton
            iconKey="actions.edit"
            label="Modifica task"
            disabled={isActionLoading}
            onClick={() => setEditing(true)}
          />
        ) : onEdit ? (
          <IconActionButton
            iconKey="actions.edit"
            label="Modifica task"
            disabled={isActionLoading}
            onClick={() => onEdit(assignment)}
          />
        ) : null}
        {canRespond ? (
          <IconActionButton
            iconKey="actions.delete"
            label={actionState.action === 'reject' && isActionLoading ? 'Rifiuto in corso' : 'Rifiuta task'}
            tone="danger"
            disabled={isActionLoading}
            onClick={() => onReject?.(assignment.id)}
          />
        ) : null}
        {canDelete && assignment.status === 'da_accettare' ? (
          <IconActionButton
            iconKey="actions.delete"
            label="Elimina task"
            tone="danger"
            disabled={isActionLoading}
            onClick={() => onDelete?.(assignment)}
          />
        ) : null}
      </>
    ) : null;

  if (editing && assignment) return <EditAssignmentDialog key={assignment.id} open={open} assignment={assignment}
    staff={staff} clients={clients} suppliers={suppliers} currentUser={currentUser}
    onSave={onSave} onClose={() => setEditing(false)} />;

  return (
    <EntityDetailDialog
      isOpen={open}
      onClose={() => onOpenChange?.(false)}
      title={title}
      subtitle={assignment ? `${formatDate(assignment.dueDate)} · ${linkedEntity}` : ''}
      avatarLabel={getInitials(title)}
      badges={
        assignment
          ? [
              {
                label: `${URGENCY_LABELS[assignment.urgency] || assignment.urgency || '—'} priorità`,
                tone: assignment.urgency === 'alta' ? 'danger' : assignment.urgency === 'bassa' ? 'info' : 'warning',
              },
              {
                label: STATUS_LABELS[assignment.status] || assignment.status || 'Stato non disponibile',
                tone: assignment.status === 'completato' ? 'success' : 'info',
              },
            ]
          : []
      }
      footer={footer}
      className="assignment-detail"
    >
      {isLoading ? (
        <div className="assignment-detail__state" role="status">
          Caricamento del task in corso…
        </div>
      ) : loadError ? (
        <div className="assignment-detail__state assignment-detail__state--error wt-inline-error" role="alert">
          {loadError}
        </div>
      ) : !assignment ? (
        <div className="assignment-detail__state" role="status">
          Il task {assignmentId ? `#${assignmentId} ` : ''}non è stato trovato o non è più disponibile.
        </div>
      ) : (
        <div className="assignment-detail__content">
          <section className="assignment-detail__section assignment-detail__section--description" aria-label="Descrizione">
            <h3>Descrizione</h3>
            <p className="assignment-detail__description select-text">{assignment.description || assignment.title || 'Nessuna descrizione disponibile.'}</p>
          </section>

          <section className="assignment-detail__section" aria-label="Informazioni">
            <h3>Informazioni</h3>
            <dl className="assignment-detail__info-grid">
              <AssignmentField label="Tipo">
                {TYPE_LABELS[assignment.type] || assignment.type || '—'}
              </AssignmentField>
              <AssignmentField label="Cliente">
                <AssignmentRelationLinks kind="client" assignment={assignment} clients={clients} />
              </AssignmentField>
              <AssignmentField label="Creata il">
                {formatDate(assignment.createdAt)}
              </AssignmentField>
              <AssignmentField label="Scadenza">
                {formatDate(assignment.dueDate)}
              </AssignmentField>
              <AssignmentField label="Richiesto da">
                <AssignmentRelationLinks kind="sender" assignment={assignment} staff={staff} currentUser={currentUser} />
              </AssignmentField>
              <AssignmentField label="Assegnato a">
                <AssignmentRelationLinks kind="recipients" assignment={assignment} staff={staff} currentUser={currentUser} />
              </AssignmentField>
              {(assignment.supplierId || assignment.supplierIds?.length || assignment.supplierName) ? <AssignmentField label="Fornitore" wide>
                <AssignmentRelationLinks kind="supplier" assignment={assignment} suppliers={suppliers} />
              </AssignmentField> : null}
            </dl>
          </section>

          <AssignmentAttachments assignment={assignment} currentUser={currentUser} />
          <ClientCustomFieldsSection
            readOnly
            formKey="deals.assignment.create"
            values={assignment.customFields || {}}
            api={ASSIGNMENT_CUSTOM_FIELDS_API}
          />
          {actionState.error ? (
            <div className="assignment-detail__action-error wt-inline-error" role="alert">
              {actionState.error}
            </div>
          ) : null}
          <AssignmentComments autoFocus={focusComments} assignment={assignment} currentUser={currentUser} staff={staff} />

        </div>
      )}
    </EntityDetailDialog>
  );
}
